import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Paper, Typography } from '@mui/material';

/**
 * The layout every expanded grid row uses.
 *
 * The three panels had each grown their own copy of the same thing: a Paper floated at 90% width
 * holding Grid items fixed at four columns, with the label and the value as two Typographies that
 * looked much the same. Nothing said which section you were reading, the columns stayed at three
 * across on a phone, and each panel had a hand-guessed pixel height the content had to fit inside.
 *
 * Sections carry a heading, fields reflow by width, and the panel sizes itself - the grids now ask
 * for 'auto' rather than naming a number.
 */
export default function DetailPanel(props) {
    const { sections, children } = props;

    return (
        <Box sx={{ padding: '16px 24px', backgroundColor: '#fbfbfc' }}>
            <Paper
                elevation={0}
                sx={{
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: 2,
                    padding: { xs: '16px', md: '20px 24px' },
                }}
            >
                {sections.map((section, index) => (
                    <Box key={section.title ?? index} sx={{ marginTop: index === 0 ? 0 : '20px' }}>
                        {index > 0 && <Divider sx={{ marginBottom: '20px' }} />}

                        {section.title &&
                            <Typography
                                variant={'subtitle2'}
                                sx={{
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    marginBottom: '12px',
                                }}
                            >
                                {section.title}
                            </Typography>
                        }

                        {/* Reflows by available width instead of holding three columns on a phone. */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                    lg: 'repeat(4, minmax(0, 1fr))',
                                },
                                columnGap: '24px',
                                rowGap: '16px',
                            }}
                        >
                            {section.fields
                                .filter((field) => field && field.value !== undefined)
                                .map((field) => (
                                    <Box key={field.label} sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant={'caption'}
                                            sx={{
                                                display: 'block',
                                                color: 'text.secondary',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                fontWeight: 600,
                                                marginBottom: '2px',
                                            }}
                                        >
                                            {field.label}
                                        </Typography>
                                        <Typography
                                            variant={'body2'}
                                            sx={{ wordBreak: 'break-word', fontWeight: 500 }}
                                        >
                                            {field.value === null || field.value === '' ? '—' : field.value}
                                        </Typography>
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                ))}

                {children}
            </Paper>
        </Box>
    );
}

DetailPanel.propTypes = {
    sections: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        fields: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.node,
        })).isRequired,
    })).isRequired,

    /** Anything a panel needs beyond fields, such as the consent controls on the user panel. */
    children: PropTypes.node,
};
